import { CURRENCY_META, type CurrencyMeta } from "./meta";

/**
 * Currency search: Arabic-aware, and fast enough to run between a keypress and
 * the next frame.
 *
 * THE INDEX IS BUILT ONCE, AT MODULE LOAD.
 *
 * Roughly 165 currencies carry four searchable strings each — Arabic name,
 * English name, country, code — so a naive implementation would normalise ~660
 * strings on every keystroke, in the critical path between the finger and the
 * result. Normalisation is deterministic and the data never changes at runtime,
 * so it runs once here and the stored form is what every keystroke compares
 * against. Only the QUERY is normalised live: one short string.
 *
 * That is the whole optimisation. No trie, no fuzzy matcher, no worker — 165
 * `includes()` calls is around ten microseconds, and anything cleverer would be
 * more code to be wrong in exchange for time nobody can perceive.
 */

/**
 * Folds the many ways Arabic writes the same word into one.
 *
 * Every rule here exists because a real person types the other form:
 *   tashkeel      مِصْر and مصر are the same word; almost nobody types marks
 *   alef forms    أ إ آ ا — Arabic keyboards make the bare alef easiest, so a
 *                 search for "امارات" must find "الإمارات"
 *   ة -> ه        typed interchangeably at the end of a word
 *   ى -> ي        the same letter to most writers, and to Egyptian keyboards
 *                 in particular
 *   tatweel       ـ is decorative stretching and carries no meaning at all
 *   Arabic-Indic  read, never written — the app's own digit rule, applied to
 *                 input so someone pasting ٥٠ is understood
 */
const TASHKEEL = /[ً-ْٰـ]/g;
const ALEF = /[آأإٱ]/g;
const ARABIC_INDIC = /[٠-٩۰-۹]/g;

export function normalize(text: string): string {
  return text
    .toLowerCase()
    .replace(TASHKEEL, "")
    .replace(ALEF, "ا")
    .replace(/ة/g, "ه")
    .replace(/ى/g, "ي")
    .replace(/ؤ/g, "و")
    .replace(/ئ/g, "ي")
    .replace(ARABIC_INDIC, (d) =>
      String.fromCharCode(
        48 + ((d.codePointAt(0) ?? 0) - (d.codePointAt(0)! >= 0x06f0 ? 0x06f0 : 0x0660)),
      ),
    )
    /* Collapse whitespace and drop punctuation people type inconsistently. */
    .replace(/[.\-_'’(),]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export interface SearchEntry {
  readonly code: string;
  readonly meta: CurrencyMeta;
  /** All searchable text, normalised, joined. Built once. */
  readonly haystack: string;
  /** Normalised forms kept apart so a code match can outrank a name match. */
  readonly normCode: string;
  readonly normNames: readonly string[];
  /** Normalised colloquial forms — what people type rather than what it is called. */
  readonly normAliases: readonly string[];
  /** Authored tie-break weight; see CurrencyMeta.prominence. */
  readonly prominence: number;
}

/**
 * The prebuilt index. Module-level and frozen: constructing it per render would
 * hand the cost straight back.
 */
export const SEARCH_INDEX: readonly SearchEntry[] = Object.values(CURRENCY_META).map((meta) => {
  const names = [
    meta.nameEn,
    meta.namePluralEn,
    meta.nameAr,
    meta.nameArIndefinite,
    meta.countryEn ?? "",
    meta.countryAr ?? "",
  ]
    .filter(Boolean)
    .map(normalize);

  const aliases = (meta.aliases ?? []).map(normalize);

  return {
    code: meta.code,
    meta,
    normCode: normalize(meta.code),
    normNames: names,
    normAliases: aliases,
    // Absent prominence sorts last rather than first: an unauthored currency
    // must never outrank one somebody thought about.
    prominence: meta.prominence ?? 999,
    haystack: [normalize(meta.code), ...names, ...aliases].join(" | "),
  };
});

/**
 * Ranking, lowest score first.
 *
 * Deliberately four tiers and no fuzzy distance. Someone typing "usd" wants USD
 * first, not "Australian Dollar" because it contains a u, an s and a d. Someone
 * typing "دولار" wants every dollar, in a stable order. Fuzzy matching earns
 * its keep on long free text, not on a 165-item list where every entry is three
 * words long.
 */
function score(entry: SearchEntry, query: string): number | null {
  if (entry.normCode === query) return 0;
  if (entry.normCode.startsWith(query)) return 1;
  for (const name of entry.normNames) {
    if (name.startsWith(query)) return 2;
  }
  /*
   * Aliases rank with a word-start name match, not above it.
   *
   * There was an extra tier here for an EXACT alias. Breaking it changed
   * nothing in any test, and the reason is that an exact alias also starts with
   * itself and so already matched one line below — the tier could not be
   * demonstrated to do anything. Code that cannot be shown to matter is code
   * that will be wrong later without anyone noticing, so it is gone. What
   * actually keeps "جنيه" on the Egyptian pound is prominence, which IS
   * demonstrable: removing it puts Oman at the top of "ريال".
   */
  for (const alias of entry.normAliases) {
    if (alias.startsWith(query)) return 3;
  }
  // Word-start inside a longer name: "riyal" should find "Saudi Riyal".
  for (const name of entry.normNames) {
    if (name.includes(` ${query}`)) return 3;
  }
  if (entry.haystack.includes(query)) return 4;
  return null;
}

export interface SearchResult {
  readonly entry: SearchEntry;
  readonly rank: number;
}

/**
 * Runs a query against the prebuilt index.
 *
 * An empty query returns everything, in the index's own order, because the
 * picker's default state is the full list — not an empty screen waiting to be
 * typed into.
 */
export function searchCurrencies(query: string): readonly SearchResult[] {
  const q = normalize(query);
  if (q === "") return SEARCH_INDEX.map((entry) => ({ entry, rank: 5 }));

  const hits: SearchResult[] = [];
  for (const entry of SEARCH_INDEX) {
    const rank = score(entry, q);
    if (rank !== null) hits.push({ entry, rank });
  }
  /*
   * Tier first, then AUTHORED PROMINENCE, then code.
   *
   * The middle term is the whole point. "ريال" is an exact alias of five
   * currencies, so the tier cannot separate them and alphabetical order would
   * hand the top of the list to Oman — deciding what a Gulf user sees for their
   * own money by accident of spelling. Code order remains as the final
   * tie-break so the sort is total and never reshuffles between keystrokes.
   */
  hits.sort(
    (a, b) =>
      a.rank - b.rank ||
      a.entry.prominence - b.entry.prominence ||
      a.entry.code.localeCompare(b.entry.code),
  );
  return hits;
}

/**
 * Direct lookup by code, built once alongside the index.
 *
 * Exists because the picker needs to render a row for a code it already knows —
 * the pinned "on your screen" group — and running the search for that is a
 * linear scan to answer a question with a known answer.
 */
export const INDEX_BY_CODE: ReadonlyMap<string, SearchEntry> = new Map(
  SEARCH_INDEX.map((entry) => [entry.code, entry]),
);
